import { useState, useEffect } from "react";
import { Button } from "@repo/o37-ui";
import { supabase } from "./supabaseClient";
import { trackAdminAction } from "./hooks/useAdminTracker";
import MFAChallenge from "./components/MFAChallenge";

type LoginProps = {
  onLoginSuccess: (isAdmin?: boolean, partnerId?: string) => void;
};

export default function Login({ onLoginSuccess }: LoginProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // MFA State
  const [mfaRequired, setMfaRequired] = useState(false);
  const [factorId, setFactorId] = useState("");

  // Suspension State
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspendedReason, setSuspendedReason] = useState("");

  // Check for suspension on mount
  useEffect(() => {
    const suspended = localStorage.getItem("app_suspended") === "true";
    const reason = localStorage.getItem("app_suspended_reason") || "";
    if (suspended) {
      setIsSuspended(true);
      setSuspendedReason(reason);
      // Clear the flags so they don't persist
      localStorage.removeItem("app_suspended");
      localStorage.removeItem("app_suspended_reason");
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.session) {
         // Check for MFA requirements
         // 1. Check if user has enrolled factors (Supabase Auth)
         const { data: factors } = await supabase.auth.mfa.listFactors();
         const enrolledFactors = factors?.all || [];
         const verifiedFactor = enrolledFactors.find(f => f.status === 'verified');

         if (verifiedFactor) {
             // User has MFA enabled -> Challenge
             setFactorId(verifiedFactor.id);
             setMfaRequired(true);
             setLoading(false);
             return;
         }

         // 2. If no factors, proceed (we don't force enrollment during login flow yet, usually done in settings)
         // Proceed to success
         await handleSuccess(data.user);
      }
    } catch (err: any) {
      await trackAdminAction('login_failed', { 
        email,
        error: err.message
      });
      
      setError(err.message || "Invalid email or password");
      setLoading(false);
    }
  };

  const handleSuccess = async (user: any) => {
     localStorage.setItem('app_user_id', user.id || '');
     localStorage.setItem('app_email', user.email || '');
     
     await trackAdminAction('login', { 
       email: user.email,
       method: 'password'
     });
     
     onLoginSuccess();
  };

  const handleMFAVerified = async () => {
      // Refresh session to ensure AAL2 is reflected if needed, though usually verifying upgrades the session automatically
      const { data } = await supabase.auth.getUser();
      if (data.user) {
          await handleSuccess(data.user);
      }
  };

  return (
    <main className="min-h-screen bg-white text-black flex flex-col">
      <section className="flex-1 flex flex-col items-center justify-center px-6 sm:px-12">
        <div className="mb-8">
          <img src="/o37-logo.svg" alt="o37" className="h-12 w-auto" />
        </div>
        <div className="w-full max-w-xl">
          <div className="rounded-2xl border border-neutral-200 p-10">
            {mfaRequired ? (
                <MFAChallenge 
                    factorId={factorId} 
                    onSuccess={handleMFAVerified}
                    onCancel={() => {
                        setMfaRequired(false);
                        setEmail('');
                        setPassword('');
                    }}
                />
            ) : (
                <>
                    <h1 className="text-3xl font-semibold mb-3">Admin Login</h1>
                    <p className="text-neutral-600 text-base mb-8">
                    Enter your credentials to access the admin portal
                    </p>

                    <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <label className="flex flex-col gap-2">
                        <span className="text-sm text-neutral-600">Email Address</span>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="border border-neutral-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-neutral-800"
                            placeholder="admin@company.com"
                            required
                            disabled={loading}
                        />
                        </label>

                        <label className="flex flex-col gap-2">
                        <span className="text-sm text-neutral-600">Password</span>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="border border-neutral-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-neutral-800"
                            placeholder="••••••••"
                            required
                            disabled={loading}
                        />
                        </label>
                    </div>

                    {isSuspended && (
                        <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                          <div className="font-semibold">Account Suspended</div>
                          <div className="mt-1">Your account has been suspended and you cannot log in.</div>
                          {suspendedReason && <div className="mt-1 text-red-500">Reason: {suspendedReason}</div>}
                        </div>
                    )}

                    {error && (
                        <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                        {error}
                        </div>
                    )}

                    <div className="mt-6">
                        <Button
                        variant="primary"
                        type="submit"
                        disabled={loading || !email || !password}
                        className="text-base py-3 h-auto w-full px-8"
                        text={loading ? "Logging in..." : "Login"}
                        />
                    </div>

                    <div className="mt-4 text-center">
                        <a
                        href="/forgot-password"
                        className="text-sm text-neutral-600 hover:text-neutral-900 underline"
                        >
                        Forgot Password?
                        </a>
                    </div>
                    </form>
                </>
            )}
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 sm:px-12 mt-24 mb-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between text-[12px] text-neutral-500">
          <div>By all means, accelerate.</div>
          <div>🏴</div>
        </div>
      </footer>
    </main>
  );
}
